import type React from "react";
import { useEffect, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { Helmet } from 'react-helmet-async';

import { supabase } from "../../lib/supabase";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import { Button } from "../../components/ui/Button";
import Input from "../../components/ui/Input";
import { uploadImageToBucket } from "../../lib/uploadImage";

type BlogPostRow = {
  id: string;
  created_at?: string;
  published_at?: string;
  title: string;
  slug: string | null;
  excerpt: string | null;
  content: string | null;
  category_name: string | null;
  category_slug: string | null;
  author_name: string | null;
  author_role: string | null;
  tags: string[] | null;
  read_time: number | null;
  is_featured: boolean;
  is_new: boolean;
  featured_image?: string | null;
  featured_image_alt?: string | null;
  views?: number | null;
  likes?: number | null;
  meta_title?: string | null;
  meta_description?: string | null;
  og_title?: string | null;
  og_description?: string | null;
  canonical_url?: string | null;
};

type SortOption = "date_desc" | "date_asc" | "views_desc" | "likes_desc";

const BlogAdmin = () => {
  const [posts, setPosts] = useState<BlogPostRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState("");
  const [filterFeatured, setFilterFeatured] = useState<"all" | "yes" | "no">(
    "all"
  );
  const [filterNew, setFilterNew] = useState<"all" | "yes" | "no">("all");
  const [filterCategory, setFilterCategory] = useState<string>("");
  const [sortBy, setSortBy] = useState<SortOption>("date_desc");

  const emptyPost: BlogPostRow = {
    id: "",
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    category_name: "",
    category_slug: "",
    author_name: "Prince Kumar",
    author_role: "Founder, Make Mind Automations",
    tags: [],
    read_time: 5,
    is_featured: false,
    is_new: true,
    featured_image: "",
    featured_image_alt: "",
    views: 0,
    likes: 0,
    meta_title: "",
    meta_description: "",
    og_title: "",
    og_description: "",
    canonical_url: "",
  };

  const [editingPost, setEditingPost] = useState<BlogPostRow | null>(null);
  const [contentImageUrl, setContentImageUrl] = useState<string | null>(null);
  const [uploadingContentImage, setUploadingContentImage] = useState(false);
  const [uploadingFeaturedImage, setUploadingFeaturedImage] = useState(false);

  // React Quill config
  const quillModules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline", "strike"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
      ["link", "image"],
      ["clean"],
    ],
  };

  const quillFormats = [
    "header",
    "bold",
    "italic",
    "underline",
    "strike",
    "list",
    "bullet",
    "align",
    "link",
    "image",
  ];

  // Load posts on mount
  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    setLoading(true);
    setError(null);

    const { data, error } = await supabase
      .from("blog_posts")
      .select("*")
      .order("published_at", { ascending: false });

    if (error) {
      console.error("Error loading posts:", error);
      setError(error.message);
    } else {
      setPosts((data || []) as BlogPostRow[]);
    }

    setLoading(false);
  };

  const startNewPost = () => {
    setEditingPost(emptyPost);
    setError(null);
    setContentImageUrl(null);
  };

  const startEditPost = (post: BlogPostRow) => {
    setError(null);
    setEditingPost({
      ...post,
      tags: post.tags || [],
    });
    setContentImageUrl(null);
  };

  const handleDelete = async (id: string) => {
    const sure = window.confirm(
      "Are you sure you want to permanently delete this post?"
    );
    if (!sure) return;

    const { error } = await supabase.from("blog_posts").delete().eq("id", id);

    if (error) {
      console.error("Error deleting post:", error);
      alert("Error deleting post: " + error.message);
      return;
    }

    await loadPosts();
    if (editingPost && editingPost.id === id) {
      setEditingPost(null);
    }
  };

  const cleanSlug = (text: string) =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingPost) return;

    if (!editingPost.title.trim()) {
      setError("Title is required.");
      return;
    }
    if (!editingPost.content?.trim()) {
      setError("Content is required.");
      return;
    }

    setError(null);
    setSaving(true);

    const finalSlug =
      editingPost.slug && editingPost.slug.trim() !== ""
        ? cleanSlug(editingPost.slug)
        : cleanSlug(editingPost.title);

    const finalExcerpt =
      editingPost.excerpt ||
      (editingPost.content || "")
        .replace(/<[^>]+>/g, "")
        .slice(0, 157) +
        "...";

    const categorySlug =
      editingPost.category_slug && editingPost.category_slug.trim() !== ""
        ? cleanSlug(editingPost.category_slug)
        : cleanSlug(editingPost.category_name || "");

    const payload = {
      title: editingPost.title,
      slug: finalSlug,
      excerpt: finalExcerpt,
      content: editingPost.content,
      category_name: editingPost.category_name,
      category_slug: categorySlug,
      author_name: editingPost.author_name,
      author_role: editingPost.author_role,
      tags: editingPost.tags || [],
      read_time: editingPost.read_time || 5,
      is_featured: editingPost.is_featured,
      is_new: editingPost.is_new,
      featured_image: editingPost.featured_image || null,
      featured_image_alt: editingPost.featured_image_alt || null,
      meta_title: editingPost.meta_title || editingPost.title,
      meta_description: editingPost.meta_description || finalExcerpt,
      og_title: editingPost.og_title || editingPost.title,
      og_description:
        editingPost.og_description ||
        editingPost.meta_description ||
        finalExcerpt,
      canonical_url: editingPost.canonical_url || null,
      published_at: new Date().toISOString(),
    };

    let resultError;
    try {
      if (editingPost.id) {
        const { error } = await supabase
          .from("blog_posts")
          .update(payload)
          .eq("id", editingPost.id);
        resultError = error;
      } else {
        const { error } = await supabase.from("blog_posts").insert(payload);
        resultError = error;
      }

      if (resultError) throw resultError;

      setEditingPost(null);
      await loadPosts();
    } catch (err: any) {
      console.error("Error saving post:", err);
      if (
        err.message?.includes(
          "duplicate key value violates unique constraint"
        )
      ) {
        setError("This slug already exists. Please change the slug or title.");
      } else {
        setError(err.message || "An error occurred while saving.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleFeaturedImageChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (!editingPost) return;
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingFeaturedImage(true);
      const url = await uploadImageToBucket(file, "featured");
      setEditingPost({ ...editingPost, featured_image: url });
    } catch (err: any) {
      console.error("Featured upload error:", err);
      setError(err.message || "Error uploading featured image.");
    } finally {
      setUploadingFeaturedImage(false);
    }
  };

  const handleContentImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploadingContentImage(true);
      const url = await uploadImageToBucket(file, "content");
      setContentImageUrl(url);
    } catch (err: any) {
      console.error("Content upload error:", err);
      setError(err.message || "Error uploading content image.");
    } finally {
      setUploadingContentImage(false);
    }
  };

  // Derived: filters + search + sort
  const filteredSortedPosts = posts
    .filter((post) => {
      if (
        search &&
        !post.title.toLowerCase().includes(search.toLowerCase()) &&
        !(post.slug || "").toLowerCase().includes(search.toLowerCase())
      ) {
        return false;
      }
      if (filterFeatured === "yes" && !post.is_featured) return false;
      if (filterFeatured === "no" && post.is_featured) return false;
      if (filterNew === "yes" && !post.is_new) return false;
      if (filterNew === "no" && post.is_new) return false;
      if (
        filterCategory &&
        (post.category_name || "")
          .toLowerCase()
          .indexOf(filterCategory.toLowerCase()) === -1
      ) {
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "date_asc":
          return (
            new Date(a.published_at || a.created_at || "").getTime() -
            new Date(b.published_at || b.created_at || "").getTime()
          );
        case "views_desc":
          return (b.views || 0) - (a.views || 0);
        case "likes_desc":
          return (b.likes || 0) - (a.likes || 0);
        case "date_desc":
        default:
          return (
            new Date(b.published_at || b.created_at || "").getTime() -
            new Date(a.published_at || a.created_at || "").getTime()
          );
      }
    });

  // Simple analytics for header
  const totalPosts = posts.length;
  const featuredCount = posts.filter((p) => p.is_featured).length;
  const newCount = posts.filter((p) => p.is_new).length;
  const totalViews = posts.reduce((sum, p) => sum + (p.views || 0), 0);
  const totalLikes = posts.reduce((sum, p) => sum + (p.likes || 0), 0);

  const seoTitle =
    editingPost?.meta_title || editingPost?.title || "Preview Title";
  const seoDescription =
    editingPost?.meta_description ||
    editingPost?.excerpt ||
    "This is how your meta description will look in Google.";
  const seoSlug = editingPost?.slug ? `/${editingPost.slug}` : "/your-slug";

  return (
    <>
    <Helmet>
  <meta name="robots" content="noindex, nofollow" />
</Helmet>

      <Header />
      <main className="min-h-screen px-4 py-8 max-w-7xl mx-auto space-y-8">
        {/* Page header */}
        <div className="flex items-center justify-between gap-4 border-b pb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              Blog Content Manager
            </h1>
            <p className="text-muted-foreground mt-1">
              Create, update, and optimize your blog posts, images, and SEO
              metadata.
            </p>
          </div>
          <Button
            onClick={startNewPost}
            className="bg-primary text-primary-foreground"
          >
            + New Post
          </Button>
        </div>

        {/* High-level stats */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="border rounded-xl bg-card shadow-sm p-4 space-y-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Total Posts
            </p>
            <p className="text-2xl font-bold">{totalPosts}</p>
            <p className="text-xs text-muted-foreground">
              All articles in your blog.
            </p>
          </div>
          <div className="border rounded-xl bg-card shadow-sm p-4 space-y-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Featured
            </p>
            <p className="text-2xl font-bold">{featuredCount}</p>
            <p className="text-xs text-muted-foreground">
              Highlighted on the blog.
            </p>
          </div>
          <div className="border rounded-xl bg-card shadow-sm p-4 space-y-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              New
            </p>
            <p className="text-2xl font-bold">{newCount}</p>
            <p className="text-xs text-muted-foreground">
              Marked as &ldquo;New&rdquo;.
            </p>
          </div>
          <div className="border rounded-xl bg-card shadow-sm p-4 space-y-1">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">
              Views / Likes
            </p>
            <p className="text-xl font-bold">
              {totalViews} <span className="text-sm font-normal">views</span>
            </p>
            <p className="text-xs text-muted-foreground">
              {totalLikes} total likes across posts.
            </p>
          </div>
        </section>

        {/* Error banner */}
        {error && (
          <div className="bg-red-50 text-red-600 border border-red-200 rounded-lg p-4 text-sm font-medium">
            ⚠️ {error}
          </div>
        )}

        {/* Filters / Search / Sort */}
        <section className="border rounded-xl p-4 bg-card shadow-sm space-y-3">
          <div className="grid gap-3 md:grid-cols-4">
            <div className="md:col-span-2">
              <label className="text-xs font-medium mb-1 block">
                Search (title or slug)
              </label>
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search posts..."
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">
                Filter Featured
              </label>
              <select
                className="w-full border rounded-md px-2 py-1 text-sm"
                value={filterFeatured}
                onChange={(e) =>
                  setFilterFeatured(e.target.value as "all" | "yes" | "no")
                }
              >
                <option value="all">All</option>
                <option value="yes">Featured only</option>
                <option value="no">Not featured</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">
                Filter New
              </label>
              <select
                className="w-full border rounded-md px-2 py-1 text-sm"
                value={filterNew}
                onChange={(e) =>
                  setFilterNew(e.target.value as "all" | "yes" | "no")
                }
              >
                <option value="all">All</option>
                <option value="yes">New only</option>
                <option value="no">Not new</option>
              </select>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-3">
            <div>
              <label className="text-xs font-medium mb-1 block">
                Filter by Category
              </label>
              <Input
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                placeholder="e.g. Automation, Tutorials..."
              />
            </div>
            <div>
              <label className="text-xs font-medium mb-1 block">
                Sort By
              </label>
              <select
                className="w-full border rounded-md px-2 py-1 text-sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
              >
                <option value="date_desc">Newest first</option>
                <option value="date_asc">Oldest first</option>
                <option value="views_desc">Most views</option>
                <option value="likes_desc">Most likes</option>
              </select>
            </div>
          </div>
        </section>

        <div className="grid gap-8 lg:grid-cols-[1fr,1.5fr]">
          {/* LEFT: posts list */}
          <section className="space-y-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              Posts
              <span className="text-sm font-normal text-muted-foreground bg-muted/20 px-2 py-0.5 rounded-full">
                {filteredSortedPosts.length}
              </span>
            </h2>

            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="h-16 bg-muted/20 rounded-lg animate-pulse"
                  />
                ))}
              </div>
            ) : filteredSortedPosts.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-xl">
                <p className="text-muted-foreground">
                  No posts match the current filters.
                </p>
                <Button variant="link" onClick={startNewPost}>
                  Create a new post
                </Button>
              </div>
            ) : (
              <div className="border rounded-xl overflow-hidden bg-card shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground">
                          Title
                        </th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden lg:table-cell">
                          Stats
                        </th>
                        <th className="text-left px-4 py-3 font-medium text-muted-foreground hidden sm:table-cell">
                          Status
                        </th>
                        <th className="text-right px-4 py-3 font-medium text-muted-foreground">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {filteredSortedPosts.map((post) => (
                        <tr
                          key={post.id}
                          className="hover:bg-muted/50 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <div className="font-medium text-foreground">
                              {post.title}
                            </div>
                            <div className="text-xs text-muted-foreground truncate max-w-[220px]">
                              /{post.slug}
                            </div>
                          </td>
                          <td className="px-4 py-3 hidden lg:table-cell text-xs text-muted-foreground">
                            {(post.views ?? 0).toLocaleString()} views ·{" "}
                            {post.likes ?? 0} likes
                          </td>
                          <td className="px-4 py-3 hidden sm:table-cell">
                            <div className="flex gap-1">
                              {post.is_featured && (
                                <span className="px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded text-[10px] font-bold">
                                  FEAT
                                </span>
                              )}
                              {post.is_new && (
                                <span className="px-1.5 py-0.5 bg-blue-100 text-blue-800 rounded text-[10px] font-bold">
                                  NEW
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => startEditPost(post)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDelete(post.id)}
                              >
                                Delete
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          {/* RIGHT: editor */}
          <section
            className={`space-y-6 border rounded-2xl p-6 bg-card shadow-sm transition-all ${
              !editingPost
                ? "opacity-50 pointer-events-none grayscale"
                : "opacity-100"
            }`}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">
                {editingPost?.id ? "Edit Post" : "Create New Post"}
              </h2>
              {editingPost && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingPost(null)}
                >
                  Close
                </Button>
              )}
            </div>

            {!editingPost ? (
              <div className="h-[400px] flex items-center justify-center text-muted-foreground text-sm border-2 border-dashed rounded-lg">
                Select a post from the list or click &ldquo;+ New Post&rdquo; to
                start editing.
              </div>
            ) : (
              <form onSubmit={handleSave} className="space-y-5">
                {/* Title / Slug / Excerpt */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Post Title
                    </label>
                    <Input
                      name="title"
                      autoComplete="off"
                      value={editingPost.title}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setEditingPost({
                          ...editingPost,
                          title: e.target.value,
                        })
                      }
                      placeholder="Enter post title..."
                      required
                      className="text-lg font-medium"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium mb-1 block">
                        Slug (URL)
                      </label>
                      <Input
                        name="slug"
                        autoComplete="off"
                        value={editingPost.slug || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEditingPost({
                            ...editingPost,
                            slug: e.target.value,
                          })
                        }
                        placeholder="auto-generated-from-title"
                        className="font-mono text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium mb-1 block">
                        Excerpt
                      </label>
                      <Input
                        name="excerpt"
                        autoComplete="off"
                        value={editingPost.excerpt || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEditingPost({
                            ...editingPost,
                            excerpt: e.target.value,
                          })
                        }
                        placeholder="Short summary for SEO..."
                      />
                    </div>
                  </div>
                </div>

                {/* Content with ReactQuill */}
                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Content
                  </label>

                  <div className="border border-input rounded-md bg-background mb-2">
                    <ReactQuill
                      theme="snow"
                      value={editingPost.content ?? ""}
                      onChange={(value: string) =>
                        setEditingPost({
                          ...editingPost,
                          content: value,
                        })
                      }
                      modules={quillModules}
                      formats={quillFormats}
                      placeholder="Write your article here..."
                      className="min-h-[260px]"
                    />
                  </div>

                  {/* Content image upload helper */}
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <div>
                      <label className="font-medium block mb-1">
                        Upload image for content
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleContentImageUpload}
                        disabled={uploadingContentImage}
                      />
                    </div>
                    {uploadingContentImage && <span>Uploading...</span>}
                    {contentImageUrl && (
                      <div className="space-y-1 max-w-xs">
                        <div>
                          Copy this URL into your content (via the image button
                          or link):
                        </div>
                        <code className="text-[10px] bg-muted px-2 py-1 rounded block truncate">
                          {contentImageUrl}
                        </code>
                      </div>
                    )}
                  </div>
                </div>

                {/* Category, Read time, Tags */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t">
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Category
                    </label>
                    <Input
                      name="category_name"
                      autoComplete="off"
                      value={editingPost.category_name || ""}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setEditingPost({
                          ...editingPost,
                          category_name: e.target.value,
                        })
                      }
                      placeholder="e.g. Automation, Strategy..."
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-1 block">
                      Read Time (mins)
                    </label>
                    <Input
                      name="read_time"
                      autoComplete="off"
                      type="number"
                      value={editingPost.read_time || 5}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setEditingPost({
                          ...editingPost,
                          read_time: parseInt(e.target.value) || 0,
                        })
                      }
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-1 block">
                    Tags (comma separated)
                  </label>
                  <Input
                    name="tags"
                    autoComplete="off"
                    value={(editingPost.tags || []).join(", ")}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setEditingPost({
                        ...editingPost,
                        tags: e.target.value
                          .split(",")
                          .map((t) => t.trim())
                          .filter(Boolean),
                      })
                    }
                    placeholder="react, supabase, automation"
                  />
                </div>

                {/* Featured image */}
                <div className="space-y-2 pt-4 border-t">
                  <label className="text-sm font-medium mb-1 block">
                    Featured Image
                  </label>
                  <div className="flex flex-wrap items-center gap-4">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFeaturedImageChange}
                      disabled={uploadingFeaturedImage}
                    />
                    {uploadingFeaturedImage && (
                      <span className="text-xs text-muted-foreground">
                        Uploading...
                      </span>
                    )}
                  </div>
                  {editingPost.featured_image && (
                    <div className="flex items-center gap-3 mt-2">
                      <img
                        src={editingPost.featured_image}
                        alt={editingPost.featured_image_alt || ""}
                        className="w-24 h-16 object-cover rounded-md border"
                      />
                      <Input
                        placeholder="Alt text for image"
                        value={editingPost.featured_image_alt || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEditingPost({
                            ...editingPost,
                            featured_image_alt: e.target.value,
                          })
                        }
                      />
                    </div>
                  )}
                </div>

                {/* Checkboxes */}
                <div className="flex flex-wrap gap-6 p-4 bg-muted/30 rounded-lg">
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input
                      name="is_featured"
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                      checked={editingPost.is_featured}
                      onChange={(e) =>
                        setEditingPost({
                          ...editingPost,
                          is_featured: e.target.checked,
                        })
                      }
                    />
                    Featured Post
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                    <input
                      name="is_new"
                      type="checkbox"
                      className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                      checked={editingPost.is_new}
                      onChange={(e) =>
                        setEditingPost({
                          ...editingPost,
                          is_new: e.target.checked,
                        })
                      }
                    />
                    Mark as New
                  </label>
                </div>

                {/* SEO Section */}
                <div className="space-y-3 pt-4 border-t">
                  <h3 className="text-sm font-semibold">SEO Settings</h3>
                  <div className="grid gap-3 md:grid-cols-2">
                    <div>
                      <label className="text-xs font-medium mb-1 block">
                        Meta Title
                      </label>
                      <Input
                        value={editingPost.meta_title || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEditingPost({
                            ...editingPost,
                            meta_title: e.target.value,
                          })
                        }
                        placeholder="Defaults to post title if empty"
                      />
                    </div>
                    <div>
                      <label className="text-xs font-medium mb-1 block">
                        Canonical URL
                      </label>
                      <Input
                        value={editingPost.canonical_url || ""}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setEditingPost({
                            ...editingPost,
                            canonical_url: e.target.value,
                          })
                        }
                        placeholder="https://makemindautomations.com/blog/slug"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium mb-1 block">
                      Meta Description
                    </label>
                    <textarea
                      className="w-full min-h-[60px] text-sm border rounded-md p-2"
                      value={editingPost.meta_description || ""}
                      onChange={(e) =>
                        setEditingPost({
                          ...editingPost,
                          meta_description: e.target.value,
                        })
                      }
                      placeholder="Short description for search results..."
                    />
                  </div>

                  <div className="border rounded-lg p-3 bg-muted/40 text-xs">
                    <div className="text-[10px] text-muted-foreground mb-1">
                      Google Preview
                    </div>
                    <div className="text-[11px] text-primary truncate">
                      {seoTitle}
                    </div>
                    <div className="text-[10px] text-green-700 truncate">
                      makemindautomations.com{seoSlug}
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1 line-clamp-2">
                      {seoDescription}
                    </div>
                  </div>
                </div>

                {/* Footer buttons */}
                <div className="flex justify-end gap-3 pt-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEditingPost(null)}
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="min-w-[120px]"
                  >
                    {saving
                      ? "Saving..."
                      : editingPost.id
                      ? "Update Post"
                      : "Publish Post"}
                  </Button>
                </div>
              </form>
            )}
          </section>
        </div>
      </main>
      <Footer />
    </>
  );
};

export default BlogAdmin;
